/**
 * Small helpers shared by the client bookings list, the booking details page
 * and the manage flows (reschedule / cancel).
 */

import { computeBookingWindow } from '../shared/computeBookingWindow';

// A booking's real start: bookingDate carries the day, and after a move the
// time lives in bookingSlot[0] (minutes from midnight) with the date at 00:00.
export const startOf = (b) => {
  const d = new Date(b && b.bookingDate);
  const slot = Array.isArray(b && b.bookingSlot) ? Number(b.bookingSlot[0]) : NaN;
  if (Number.isFinite(slot)) d.setHours(Math.floor(slot / 60), slot % 60, 0, 0);
  return d;
};

// bookingSlot[0] is where the stylist's diary block starts, which includes the
// first service's setup time (a 40 minute setup on a 7pm cut is stored as
// 6:20pm). The client only ever booked, and should only ever see, the time
// the service itself starts. Every client-facing time goes through these two.
export const setupMinutesOf = (b) => {
  const list = (b && (b.services || b.servicesBooked)) || [];
  const first = Array.isArray(list) ? list[0] : null;
  const n = Number(first && first.setupTime);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
export const clientStartMinutes = (b) => {
  const slot = Array.isArray(b && b.bookingSlot) ? Number(b.bookingSlot[0]) : NaN;
  return Number.isFinite(slot) ? slot + setupMinutesOf(b) : null;
};
export const clientStartOf = (b) => new Date(startOf(b).getTime() + setupMinutesOf(b) * 60000);

export const cleanupMinutesOf = (b) => {
  const list = (b && (b.services || b.servicesBooked)) || [];
  const last = Array.isArray(list) && list.length ? list[list.length - 1] : null;
  const n = Number(last && last.cleanupTime);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
// How long the client is there: the stored diary block minus the setup and
// cleanup buffers (internal, never shown to clients). The block already
// includes processing gaps and finishing, so this stays right for colour
// bookings (apply → develop → finish) without re-deriving the layout. Falls
// back to the services' own timings when there's no usable slot.
export const clientMinutesOf = (b) => {
  const slot = b && b.bookingSlot;
  if (Array.isArray(slot)) {
    const start = Number(slot[0]);
    const end = Number(slot[1]);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      const m = end - start - setupMinutesOf(b) - cleanupMinutesOf(b);
      if (m > 0) return m;
    }
  }
  const list = (b && (b.services || b.servicesBooked)) || [];
  const w = computeBookingWindow(Array.isArray(list) ? list : []);
  return w.totalServiceMinutes + w.totalProcessingMinutes + w.totalFinishingMinutes;
};

// "9:50am"
export const fmtTime12 = (d) => `${((d.getHours() + 11) % 12) + 1}:${String(d.getMinutes()).padStart(2, '0')}${d.getHours() >= 12 ? 'pm' : 'am'}`;

// "Tue 22 Sept, 9:20am"
export const fmtShort = (d) => `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${((d.getHours() + 11) % 12) + 1}:${String(d.getMinutes()).padStart(2, '0')}${d.getHours() >= 12 ? 'pm' : 'am'}`;

// Bookings moved in this session: the card says "Moved" until the tab closes,
// so the list reflects what just happened when they come back from the receipt.
const MOVED_KEY = 'beu:movedBookings';
export const movedIds = () => { try { return JSON.parse(sessionStorage.getItem(MOVED_KEY) || '[]'); } catch (e) { return []; } };
export const rememberMoved = (id) => { try { sessionStorage.setItem(MOVED_KEY, JSON.stringify([...new Set([...movedIds(), String(id)])])); } catch (e) { /* ignore */ } };

export const salonPhoneOf = (booking) => {
  const salon = (booking && booking.salonInfo) || {};
  return salon.phone || salon.phoneNumber || '';
};

// bookingStatus: 0=Pending, 1=booked, 2=confirmed, 3=cancelled, 4=completed, 5=rescheduled, 6=no show
export const bookingStatusOf = (booking) => {
  if (!booking) return { text: '', class: '' };
  if (booking.bookingStatus === 3 || booking.status === 'cancelled') return { text: 'Cancelled', class: 'cancelled' };
  if (booking.bookingStatus === 4 || booking.status === 'completed') return { text: 'Complete', class: 'complete' };
  if (movedIds().includes(String(booking._id))) return { text: 'Moved', class: 'confirmed' };
  if (booking.bookingStatus === 2 || booking.status === 'confirmed') return { text: 'Confirmed', class: 'confirmed' };
  if (booking.bookingStatus === 0 || booking.status === 'pending') return { text: 'Pending', class: 'pending' };
  if (booking.bookingStatus === 1) return { text: 'Booked', class: 'confirmed' };
  if (booking.bookingStatus === 5) return { text: 'Rescheduled', class: 'pending' };
  if (booking.bookingStatus === 6) return { text: 'No Show', class: 'cancelled' };
  return { text: 'Complete', class: 'complete' };
};

// Reschedule deadline = start minus the policy hours. The preview also returns
// rescheduleBy, but on staging it runs an hour late (a UTC offset slip on the
// backend), so we only fall back to it when the hours are missing.
export const rescheduleDeadline = (booking, preview) => {
  const cur = booking && booking.bookingDate ? startOf(booking) : null;
  const hours = Number(preview && preview.rescheduleBeforeHours);
  const byIso = preview && preview.rescheduleBy;
  const by = cur && Number.isFinite(hours) && hours > 0 ? new Date(cur.getTime() - hours * 3600000) : (byIso ? new Date(byIso) : null);
  return by && !Number.isNaN(by.getTime()) ? by : null;
};

// Our own words for a blocked reschedule, not the API's reason string. The
// backend says "closed N hours before" for the cut-off, "already started" once
// the start time has passed, and "isn't available" when the salon (or that
// stylist) has rescheduling off. Anything else falls back to the API's own
// words with its em dash rewritten.
export const blockedRescheduleCopy = (reason) => {
  const r = String(reason || '');
  const hours = (r.match(/(\d+)\s*hour/i) || [])[1];
  if (/already started|has started|in the past|already passed/i.test(r)) return 'This appointment has already started.';
  if (/clos/i.test(r)) return hours ? `Online rescheduling closes ${hours} hours before your appointment.` : 'Online rescheduling has closed for this appointment.';
  if (/not available|isn't available|not offer|disabled|turned off/i.test(r) || !r.trim()) return 'This salon does not offer online rescheduling.';
  return r.replace(/\s*[\u2014\u2013]\s*(\w)/g, (m, c) => `. ${c.toUpperCase()}`).replace(/\.\s*\./g, '.').replace(/([^.!?])$/, '$1.');
};

/* ---------- List grouping and labels (dashboard bookings list) ---------- */

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const daysFromToday = (d) => Math.round((dayStart(d) - dayStart(new Date())) / 86400000);
const sameYear = (d) => d.getFullYear() === new Date().getFullYear();

// Day headings: "Today", "Tomorrow", then "Wednesday 23 September" (the year
// only when it is not this one).
export const dayLabel = (d) => {
  const n = daysFromToday(d);
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-GB', sameYear(d) ? { weekday: 'long', day: 'numeric', month: 'long' } : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
export const dayKey = (d) => d.toLocaleDateString('en-CA');

// Month headings for past bookings: "September", "August 2025".
export const monthLabel = (d) => d.toLocaleDateString('en-GB', sameYear(d) ? { month: 'long' } : { month: 'long', year: 'numeric' });
export const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// The chip on the Next up card.
export const countdownLabel = (d) => {
  const n = daysFromToday(d);
  if (n <= 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n < 14) return `In ${n} days`;
  if (n < 60) return `In ${Math.round(n / 7)} weeks`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const sortByStart = (list, dir = 1) => [...list].sort((a, b) => dir * (clientStartOf(a).getTime() - clientStartOf(b).getTime()));

// Groups in order of first appearance, so pass a sorted list.
export const groupBookings = (list, keyOf, labelOf) => {
  const groups = [];
  const byKey = {};
  list.forEach((b) => {
    const d = clientStartOf(b);
    const key = keyOf(d);
    if (!byKey[key]) { byKey[key] = { key, label: labelOf(d), items: [] }; groups.push(byKey[key]); }
    byKey[key].items.push(b);
  });
  return groups;
};

export const servicesOf = (b) => (b && (b.servicesBooked || b.services)) || [];
export const serviceLabelOf = (b) => {
  const names = servicesOf(b).map((s) => s.serviceName || s.name).filter(Boolean);
  if (!names.length) return 'Appointment';
  return names.length > 1 ? `${names[0]} +${names.length - 1} more` : names[0];
};
export const stylistNameOf = (b) => { const h = (b && (b.hairdresserInfo || b.hairdresser)) || {}; return (h.name || h.first_name || '').trim(); };
export const salonNameOf = (b) => { const s = (b && b.salonInfo) || {}; return s.salonName || s.name || ''; };
export const initialsOf = (name) => (name || '').split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?';

// Thumbnail chain: salon image, then the stylist's avatar, then initials.
export const thumbOf = (b) => {
  const salon = (b && b.salonInfo) || {};
  const h = (b && (b.hairdresserInfo || b.hairdresser)) || {};
  const ui = h.userImage || {};
  const src = salon.salonImage || salon.image || ui.circleImage || ui.squareImage || h.avatar || h.image || '';
  return { src, initials: initialsOf(stylistNameOf(b) || salonNameOf(b)) };
};
export const avatarOf = (b) => {
  const h = (b && (b.hairdresserInfo || b.hairdresser)) || {};
  const ui = h.userImage || {};
  return { src: ui.circleImage || ui.squareImage || h.avatar || h.image || '', initials: initialsOf(stylistNameOf(b)) };
};

const money = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;
// "£50.00 · £10.00 paid, £40.00 at the salon"
export const moneyLineOf = (b) => {
  if (!b) return '';
  const sym = b.currencySymbol || '£';
  const total = Number(b.totalAmount || b.totalPrice || 0);
  const dep = b.isDepositPay ? Number(b.depositAmount || 0) : 0;
  if (dep > 0 && dep < total) return `${money(sym, total)} · ${money(sym, dep)} paid, ${money(sym, total - dep)} at the salon`;
  if (dep > 0 && dep >= total) return `${money(sym, total)} · paid`;
  return money(sym, total);
};

export const isCancelled = (b) => !!b && (b.bookingStatus === 3 || b.status === 'cancelled');
export const hasStarted = (b) => !!b && startOf(b).getTime() <= Date.now();

// Pill for a list row, only when the state is out of the ordinary. A booking
// that is simply booked, confirmed, in progress or complete gets no pill:
// those are the normal course of things and the label told the client
// nothing. tone: teal (default), muted, amber.
export const listStatusOf = (b, { past = false, needsConfirm = false } = {}) => {
  if (!b) return null;
  if (isCancelled(b)) return { text: 'Cancelled', tone: 'muted' };
  if (b.bookingStatus === 6) return { text: 'Missed', tone: 'muted' };
  if (past || b.bookingStatus === 4 || b.status === 'completed') return null;
  if (needsConfirm) return { text: 'Confirm', tone: 'amber' };
  if (b.bookingStatus === 0 || b.status === 'pending') return { text: 'Pending', tone: 'amber' };
  if (hasStarted(b)) return null;
  if (movedIds().includes(String(b._id))) return { text: 'Moved', tone: 'teal' };
  if (b.bookingStatus === 5) return { text: 'Rescheduled', tone: 'teal' };
  return null;
};

/* ---------- Images and durations shared by the list and the details page ---------- */

export const isImg = (u) => typeof u === 'string' && /^https?:\/\//.test(u) && !u.includes('placeholder');

// The salon's headline image: the first gallery photo, the one the salon
// framed with the crop guides in Settings, falling back to its profile image.
export const headlineOf = (salon) => {
  if (!salon) return '';
  const g = (salon.galleryImages || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const first = g.find((x) => isImg(x && x.url));
  return first ? first.url : (isImg(salon.image) ? salon.image : '');
};

// "1h 30m", "45 min"
export const fmtDuration = (minutes) => {
  const n = Number(minutes) || 0;
  if (!n) return '';
  if (n >= 60) { const h = Math.floor(n / 60); const m = n % 60; return m > 0 ? `${h}h ${m}m` : `${h}h`; }
  return `${n} min`;
};
